import { api, ApiError } from './api';

/**
 * Sign in with Apple in the iPhone and iPad app: the system's own sheet
 * (Face ID, the Apple Account the device is signed in with), through
 * @capawesome/capacitor-apple-sign-in. No browser is involved and the app
 * never leaves the screen, so the result is told here rather than in a
 * return address (App.tsx listens to `signInResults`).
 *
 *   1. The server gives a one-time value (nonce).
 *   2. The sheet asks Apple, which signs a token carrying that value.
 *   3. The server checks the token and the code with Apple, and signs in.
 *
 * The website and the Android app use Apple's page instead (startSignIn).
 */

/** What the sheet hands back (the plugin's result, the fields used). */
export interface AppleSheetAnswer {
  idToken: string;
  authorizationCode: string;
  givenName?: string | null;
  familyName?: string | null;
}

/** Asks the sheet: the plugin's signIn, or a stand-in in automated tests. */
type Sheet = (request: { nonce: string; scopes: string[] }) => Promise<AppleSheetAnswer>;

/** How a sign-in that never left the app ended: 'created', 'returning' or 'linked', or an error code (authErrors.ts). */
export interface SignInResult {
  outcome?: string;
  error?: string;
}

/** Told how each sign-in through the sheet ended. Cancelling tells nothing: there is nothing to say. */
export const signInResults = new EventTarget();

function announce(result: SignInResult): void {
  signInResults.dispatchEvent(new CustomEvent<SignInResult>('result', { detail: result }));
}

let standIn: Sheet | null = null;
/**
 * Replaces the system sheet with `sheet` (null puts it back). Only for the
 * automated tests of the apps (automation.ts), whose builds cannot show the
 * real sheet: an unsigned simulator build has no Sign in with Apple.
 */
export function setAppleSheetForTests(sheet: Sheet | null): void {
  standIn = sheet;
}

async function systemSheet(request: { nonce: string; scopes: string[] }): Promise<AppleSheetAnswer> {
  const { AppleSignIn, SignInScope } = await import('@capawesome/capacitor-apple-sign-in');
  // The nonce is passed as it is: Apple writes it into the token it signs, where the server looks for it.
  return AppleSignIn.signIn({ scopes: [SignInScope.Email, SignInScope.FullName], nonce: request.nonce });
}

/** The person closed the sheet: Apple's plugin says SIGN_IN_CANCELED (or the system's own code for it, 1001). */
function cancelled(err: unknown): boolean {
  const code = String((err as { code?: unknown } | null)?.code ?? '');
  return code === 'SIGN_IN_CANCELED' || code === '1001';
}

const failure = (err: unknown): string => (err instanceof ApiError ? (err.code === 'code_invalid' ? 'apple_sheet_expired' : err.code) : 'network');

/** Signs in with the system's Sign in with Apple sheet. Resolves once it has ended, however it ended. */
export async function signInWithAppleSheet(): Promise<void> {
  let nonce: string;
  try {
    ({ nonce } = await api.request<{ nonce: string }>('/api/auth/apple/native/start', { method: 'POST', auth: 'none', body: {} }));
  } catch (err) {
    return announce({ error: failure(err) });
  }
  let answer: AppleSheetAnswer;
  try {
    answer = await (standIn ?? systemSheet)({ nonce, scopes: ['EMAIL', 'FULL_NAME'] });
  } catch (err) {
    if (!cancelled(err)) announce({ error: 'apple_sheet_failed' });
    return;
  }
  try {
    const outcome = await api.finishAppleSheet({
      identityToken: answer.idToken,
      authorizationCode: answer.authorizationCode,
      givenName: answer.givenName ?? null,
      familyName: answer.familyName ?? null,
    });
    announce({ outcome });
  } catch (err) {
    announce({ error: failure(err) });
  }
}
